import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
} from 'lucide-react';
import clsx from 'clsx';

/**
 * Rich text editor for editorial review (NWFE-RQ-01.2).
 * Scope per client decision: standard formatting (bold, italic, lists) +
 * basic formula text (sub/superscript) — no LaTeX/MathML in Phase 1.
 */

interface RichTextEditorProps {
  value: string; // HTML
  onChange: (html: string) => void;
  placeholder?: string;
  compact?: boolean; // smaller min-height for alternatives
  disabled?: boolean;
}

export function RichTextEditor({ value, onChange, compact, disabled }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit, Subscript, Superscript],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Keep the editor in sync when the loaded value changes (e.g. after fetch)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && value !== undefined) {
      // Avoid clobbering while the user is typing: only set when out of focus
      if (!editor.isFocused) {
        editor.commands.setContent(value || '', { emitUpdate: false });
      }
    }
  }, [value, editor]);

  if (!editor) return null;

  const buttons = [
    {
      icon: Bold,
      title: 'Bold',
      isActive: () => editor.isActive('bold'),
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      icon: Italic,
      title: 'Italic',
      isActive: () => editor.isActive('italic'),
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      icon: UnderlineIcon,
      title: 'Underline',
      isActive: () => editor.isActive('underline'),
      run: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      icon: List,
      title: 'Bullet list',
      isActive: () => editor.isActive('bulletList'),
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      icon: ListOrdered,
      title: 'Numbered list',
      isActive: () => editor.isActive('orderedList'),
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      icon: SubscriptIcon,
      title: 'Subscript',
      isActive: () => editor.isActive('subscript'),
      run: () => editor.chain().focus().toggleSubscript().run(),
    },
    {
      icon: SuperscriptIcon,
      title: 'Superscript',
      isActive: () => editor.isActive('superscript'),
      run: () => editor.chain().focus().toggleSuperscript().run(),
    },
  ];

  return (
    <div
      className={clsx(
        'border rounded-xl overflow-hidden bg-white transition-colors',
        disabled ? 'border-gray-100 bg-gray-50' : 'border-gray-200 focus-within:border-[#1e73be]'
      )}
    >
      {!disabled && (
        <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50/70">
          {buttons.map((btn) => (
            <button
              key={btn.title}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={btn.run}
              title={btn.title}
              className={clsx(
                'p-1.5 rounded-lg transition-colors',
                btn.isActive()
                  ? 'bg-[#1e73be] text-white'
                  : 'text-gray-500 hover:text-[#1e73be] hover:bg-blue-50'
              )}
            >
              <btn.icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      )}
      <EditorContent
        editor={editor}
        className={clsx(
          'prose prose-sm max-w-none px-3 py-2 text-sm text-gray-800',
          '[&_.ProseMirror]:outline-none [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:pl-5',
          compact ? '[&_.ProseMirror]:min-h-[48px]' : '[&_.ProseMirror]:min-h-[110px]'
        )}
      />
    </div>
  );
}
